import React from 'react';
import { TrendingUp, TrendingDown, Minus, ExternalLink } from 'lucide-react';
import { Recommendation } from '../../types';
import { useNavigate } from 'react-router-dom';
import { getStockScoreBadgeClass } from '../../utils';

interface StockCardProps {
  recommendation: Recommendation;
  variant?: 'default' | 'compact' | 'featured';
}

export const StockCard: React.FC<StockCardProps> = ({ recommendation, variant = 'default' }) => {
  const navigate = useNavigate();
  const upside = recommendation.potentialUpside || recommendation.potential_value || 0;
  const isPositive = upside > 0;
  const isBuy = recommendation.recommendation === 'Buy';
  const isSell = recommendation.recommendation === 'Sell';
  const isHold = recommendation.recommendation === 'Hold';

  // Helper to safely format numbers
  const formatPrice = (price: any): string => {
    if (price === null || price === undefined) return 'N/A';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return isNaN(num) ? 'N/A' : num.toFixed(2);
  };

  const targetPrice = formatPrice(recommendation.targetPrice || recommendation.target_price);
  const currentPrice = formatPrice(recommendation.stock?.current_price || recommendation.current_price);

  const getRecommendationColor = () => {
    if (isBuy) return 'from-green-500 to-green-600';
    if (isSell) return 'from-red-500 to-rose-600';
    return 'from-amber-500 to-orange-600';
  };

  const getRecommendationBg = () => {
    if (isBuy) return 'bg-green-50 border-green-200';
    if (isSell) return 'bg-red-50 border-red-200';
    return 'bg-amber-50 border-amber-200';
  };

  const getRecommendationText = () => {
    if (isBuy) return 'text-green-700';
    if (isSell) return 'text-red-700';
    return 'text-amber-700';
  };

  const handleClick = () => {
    const symbol = recommendation.stock?.symbol || recommendation.company?.seo_name || recommendation.company?.name;
    if (symbol) {
      navigate(`/stock/${symbol}`);
    }
  };

  if (variant === 'compact') {
    return (
      <div 
        onClick={handleClick}
        className="group bg-white hover:bg-gradient-to-br hover:from-white hover:to-blue-50 rounded-xl p-4 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 cursor-pointer"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold text-gray-900 text-sm truncate">
                {recommendation.stock?.symbol || 'N/A'}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRecommendationBg()} ${getRecommendationText()}`}>
                {recommendation.recommendation}
              </span>
              {/* Updated badge design with icon */}
              {(recommendation as any).stock_score !== undefined && (recommendation as any).stock_score !== null && (
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full ${getStockScoreBadgeClass((recommendation as any).stock_score)} text-xs font-bold shadow-sm border border-white/20`}>
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  {(typeof (recommendation as any).stock_score === 'string') ? parseFloat((recommendation as any).stock_score).toFixed(1) : (recommendation as any).stock_score.toFixed(1)}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 truncate">{recommendation.brokerage?.name || 'N/A'}</p>
          </div>
          <div className="text-right ml-3">
            <div className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
              {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {upside > 0 ? '+' : ''}{typeof upside === 'number' ? upside.toFixed(2) : '0.00'}%
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'featured') {
    return (
      <div 
        onClick={handleClick}
        className="group relative bg-gradient-to-br from-white via-blue-50 to-indigo-100 rounded-2xl p-4 sm:p-6 border-2 border-white hover:border-blue-300 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden min-w-[240px] max-w-[98vw] sm:min-w-[280px] sm:max-w-sm"
      >
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-400/5 to-indigo-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-2xl font-bold text-gray-900 mb-1 truncate">
                {recommendation.stock?.name || 'N/A'}
              </h3>
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-600 truncate">{recommendation.stock?.symbol || 'Company Name'}</p>
                {/* Updated featured badge with premium styling */}
                {(recommendation as any).stock_score !== undefined && (recommendation as any).stock_score !== null && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${getStockScoreBadgeClass((recommendation as any).stock_score)} text-sm font-bold shadow-md border-2 border-white/30`}>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    {typeof (recommendation as any).stock_score === 'string' ? parseFloat((recommendation as any).stock_score).toFixed(1) : (recommendation as any).stock_score.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
            <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
          </div>

          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r ${getRecommendationColor()} mb-4`}>
            {isBuy && <TrendingUp className="h-4 w-4 text-white" />}
            {isSell && <TrendingDown className="h-4 w-4 text-white" />}
            {isHold && <Minus className="h-4 w-4 text-white" />}
            <span className="text-white font-semibold text-sm">{recommendation.recommendation}</span>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Target Price</p>
              <p className="text-lg font-bold text-gray-900">
                ₹{targetPrice}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Current Price</p>
              <p className="text-lg font-bold text-gray-900">
                ₹{currentPrice}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-white/60 backdrop-blur-sm rounded-lg">
            <span className="text-sm text-gray-600">{recommendation.brokerage?.name || 'N/A'}</span>
            <div className={`text-xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
              {isPositive ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
              {upside > 0 ? '+' : ''}{typeof upside === 'number' ? upside.toFixed(2) : '0.00'}%
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={handleClick}
      className="group bg-white hover:bg-gradient-to-br hover:from-white hover:to-blue-50 rounded-xl p-4 sm:p-5 border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 cursor-pointer min-w-[220px] max-w-[95vw] sm:min-w-[240px] sm:max-w-xs"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-gray-900 mb-1 truncate">
            {recommendation.stock?.symbol || 'N/A'}
          </h3>
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-600 truncate">{recommendation.stock?.name || 'Company Name'}</p>
            {/* Updated default badge with modern styling */}
            {(recommendation as any).stock_score !== undefined && (recommendation as any).stock_score !== null && (
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full ${getStockScoreBadgeClass((recommendation as any).stock_score)} text-xs font-bold shadow-sm border border-white/20`}>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                {(typeof (recommendation as any).stock_score === 'string') ? parseFloat((recommendation as any).stock_score).toFixed(1) : (recommendation as any).stock_score.toFixed(1)}
              </span>
            )}
          </div>
          {recommendation.stock?.sector && (
            <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              {recommendation.stock.sector}
            </span>
          )}
        </div>
        <div className={`px-3 py-1.5 rounded-lg ${getRecommendationBg()} border`}>
          <span className={`font-semibold text-sm ${getRecommendationText()}`}>
            {recommendation.recommendation}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Target</p>
          <p className="text-base font-semibold text-gray-900">
            ₹{targetPrice}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Current</p>
          <p className="text-base font-semibold text-gray-900">
            ₹{currentPrice}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-bold">
              {recommendation.brokerage?.name?.substring(0, 2).toUpperCase() || 'N/A'}
            </span>
          </div>
          <span className="text-sm text-gray-600 truncate max-w-[120px]">
            {recommendation.brokerage?.name || 'N/A'}
          </span>
        </div>
        <div className={`text-lg font-bold ${isPositive ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
          {isPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          {upside > 0 ? '+' : ''}{typeof upside === 'number' ? upside.toFixed(2) : '0.00'}%
        </div>
      </div>
    </div>
  );
};